/* eslint-disable @typescript-eslint/naming-convention */
// helpers
import { isEmpty } from "@swiggy-private/js-utils";
import { getCartMenuItemPropertyForUpdate, getCartSavedPreferences } from "./Cart";
import { getSelectedAddressFromReviewedCart, hasValidCoupon } from "./Cart";
import { getTipDetails } from "./Cart";
import { isValidNumber, format } from "@swiggy-private/core-ui";
import { getFoodCartItemsForUpdateCartAPI } from "@cart-client/helpers/GiftingFoodSubCart";
import { getIMCartItemsForUpdateCartAPI, isAnyIMItem } from "@cart-client/helpers/GiftingIMSubCart";
import { isFoodItem } from "@cart-client/helpers/GiftingFoodSubCart";
import { getUniqueIdForCartItem } from "@cart-client/helpers/CartItemRenderer";
// constants
import { STATIC_TEXTS } from "@cart-client/constants/GiftingCart";
// types
import {
    ApiMenuCartItem,
    CartItem,
    CombinedCartInStockItemRender,
    GiftingCartApiResponse,
    GiftingLocalCart,
    IMFilteredStructureItem,
    IMStructureItem,
    FoodCartItemWithUUID,
    IMCartItemWithUUID,
    GiftingCartItemType,
    ItemLimitExceededItem,
} from "@swiggy-private/cart-types";
import { GiftingReviewedCart } from "@cart-client/interfaces/GiftingCart";
import { GetGiftingLocalCartResponsePayload } from "@swiggy-private/native-bridge";
import { Item } from "@swiggy-private/dls-web-recipe-common-web/dist/components/ReviewItemListCard";
import { CART_TYPE } from "@swiggy-private/checkout-core";
import { INTERNAL_CART_SOURCE } from "@cart-client/interfaces/Cart";

export const isGiftingCartSource = (cartSource?: INTERNAL_CART_SOURCE): boolean => {
    return cartSource === INTERNAL_CART_SOURCE.GIFTING;
};

export const isGiftingCartEmpty = (cartData?: GiftingCartApiResponse | null): boolean => {
    return isEmpty(cartData?.imCartItems) && isEmpty(cartData?.foodCartItems?.items);
};

export const slaTimerStringBuilder = (slaTime: number) => {
    if (slaTime < 1 || !isValidNumber(slaTime)) {
        return "";
    }
    if (slaTime === 1) {
        return format(STATIC_TEXTS.MINUTE, String(slaTime));
    }
    return format(STATIC_TEXTS.MINUTES, String(slaTime));
};

export function prepareNativeGiftingCartParamsFromReviewedCartResponse(
    cartData: GiftingCartApiResponse,
    localCartData?: GiftingLocalCart,
): GiftingLocalCart {
    const isCouponValid = hasValidCoupon(cartData);
    const tipDetails = getTipDetails(cartData);
    const selectedAddress = getSelectedAddressFromReviewedCart(cartData);
    const cart_saved_preferences = getCartSavedPreferences(cartData);
    const foodCartItems = getFoodCartItemsForUpdateCartAPI(cartData?.foodCartItems?.items);
    const imCartItems = getIMCartItemsForUpdateCartAPI(cartData?.imCartItems);
    const nativeCartParams: GiftingLocalCart = {
        restaurantId: cartData?.restaurant_details?.id,
        restaurantName: cartData?.restaurant_details?.name,
        restaurantCloudinaryImageId: cartData?.restaurant_details?.cloudinary_image_id,
        originalCartId: cartData?.originalCartId,
        address_id: cartData?.address_id,
        foodCartItems,
        imCartItems,
        isCouponValid,
        tipDetails,
        cart_saved_preferences: cart_saved_preferences ?? {},
        cart_id: cartData?.cartId,
        orderComment: localCartData?.orderComment || "",
        cutleryComments: localCartData?.cutleryComments || "",
        cartLatLong: localCartData?.cartLatLong,
        sld: localCartData?.sld,
        userLiveLat: localCartData?.userLiveLat,
        userLiveLng: localCartData?.userLiveLng,
        date: localCartData?.date,
        cart_journey_ctx: {
            cart_initialization_source: localCartData?.cart_journey_ctx?.cart_initialization_source,
            cart_category_type: localCartData?.cart_journey_ctx?.cart_category_type,
            request_contexts: localCartData?.cart_journey_ctx?.request_contexts,
        },
        useGiftBagPackaging: localCartData?.useGiftBagPackaging,
        couponCodes: cartData?.couponCodes ?? [],
        cart_type: localCartData?.cart_type ?? CART_TYPE.GIFTING,
    };

    if (!isEmpty(localCartData?.sld)) {
        nativeCartParams.sldRestaurantId = cartData.restaurant_details?.id;
    }

    if (!isEmpty(selectedAddress)) {
        nativeCartParams.selectedAddress = selectedAddress;
    }

    return nativeCartParams;
}

export const generateNativeLocalGiftingCartFromStates = (
    reviewedCartData: GiftingCartApiResponse,
    localCart?: GiftingLocalCart | null,
) => {
    const localCartPayload = prepareNativeGiftingCartParamsFromReviewedCartResponse(reviewedCartData, {
        orderComment: localCart?.orderComment,
        cutleryComments: localCart?.cutleryComments,
        cartLatLong: localCart?.cartLatLong,
        sld: localCart?.sld,
        userLiveLat: localCart?.userLiveLat,
        userLiveLng: localCart?.userLiveLng,
        cart_journey_ctx: {
            cart_initialization_source: localCart?.cart_journey_ctx?.cart_initialization_source,
            cart_category_type: localCart?.cart_journey_ctx?.cart_category_type,
            request_contexts: localCart?.cart_journey_ctx?.request_contexts,
        },
        useGiftBagPackaging: localCart?.useGiftBagPackaging,
        cart_type: localCart?.cart_type ?? CART_TYPE.GIFTING,
    });
    return localCartPayload;
};

export const convertNativeGiftingCartToUpdateCartAPIPayload = (
    payload: GetGiftingLocalCartResponsePayload,
): GiftingLocalCart => {
    return {
        ...payload,
        imCartItems: getIMCartItemsForUpdateCartAPI(payload.imCartItems as IMStructureItem[]),
    };
};

export const validatePriceAndPrepareForRender = (price?: number | string) => {
    if (isValidNumber(price) && price !== 0) {
        return `₹${price}`;
    }
    return "";
};

export const sortSubCartsBySLA = (subCarts?: CombinedCartInStockItemRender[]): CombinedCartInStockItemRender[] => {
    if (isEmpty(subCarts)) {
        return [];
    }
    return (
        subCarts?.sort((a, b) => {
            if (a?.slaTime === b?.slaTime || !isValidNumber(a?.slaTime) || !isValidNumber(b?.slaTime)) {
                return 0;
            }
            return a?.slaTime < b?.slaTime ? -1 : 1;
        }) || []
    );
};

export const sortItemsBasedOnItemPosition = (itemsList: FoodCartItemWithUUID[] | IMCartItemWithUUID[]) => {
    return itemsList.sort(
        (a: FoodCartItemWithUUID | IMCartItemWithUUID, b: FoodCartItemWithUUID | IMCartItemWithUUID) => {
            if (a.itemPosition > b.itemPosition) {
                return 1;
            }
            return b.itemPosition > a.itemPosition ? -1 : 0;
        },
    );
};

export const getFoodIMItemsListForGiftingCartUpdate = (
    itemsList: FoodCartItemWithUUID[] | IMCartItemWithUUID[],
    itemSubType: GiftingCartItemType,
): (ApiMenuCartItem | IMFilteredStructureItem)[] => {
    let updatedList: (ApiMenuCartItem | IMFilteredStructureItem)[] = [];
    if (!isEmpty(itemsList)) {
        // sort items based on position to maintain their position level
        const sortedItemList = sortItemsBasedOnItemPosition(itemsList);
        if (isAnyIMItem(itemSubType)) {
            const modifiedIMItem = getIMCartItemsForUpdateCartAPI(sortedItemList as IMCartItemWithUUID[]);
            if (modifiedIMItem && modifiedIMItem.length > 0) {
                updatedList = [...(modifiedIMItem as IMFilteredStructureItem[])];
            }
        } else if (isFoodItem(itemSubType)) {
            (sortedItemList as FoodCartItemWithUUID[]).map((item: FoodCartItemWithUUID) => {
                updatedList.push(getCartMenuItemPropertyForUpdate(item) as ApiMenuCartItem);
            });
        }
    }
    return updatedList;
};

export const createGiftingCartItemListWithUUID = (
    cartItems: CartItem[] | IMStructureItem[],
): (FoodCartItemWithUUID | IMCartItemWithUUID)[] => {
    const list: (FoodCartItemWithUUID | IMCartItemWithUUID)[] = [];

    // if no items return empty list
    if (isEmpty(cartItems)) {
        return list;
    }

    // iterate over items and push to result list
    cartItems.forEach((item: CartItem | IMStructureItem, index: number) => {
        const itemId = ("menu_item_id" in item ? item.menu_item_id : item.itemId) || "";
        list.push({
            ...item,
            unId: getUniqueIdForCartItem(String(itemId), index),
            itemPosition: index,
        });
    });

    return list;
};

export const fetchGiftingCartItemsFromResponse = (cartResponse: GiftingReviewedCart) => {
    if (!isEmpty(cartResponse.data?.foodCartItems?.items)) {
        return cartResponse.data?.foodCartItems?.items || [];
    }
    return [];
};

export const calcQuantityCountForItems = (items: Item[]) => {
    return (
        items?.reduce((acc: number, item: Item) => {
            acc += item.quantity || 0;
            return acc;
        }, 0) ?? 0
    );
};

export const getItemLimitExceededList = (cartResponse: GiftingReviewedCart | null): ItemLimitExceededItem[] => {
    const itemIds = cartResponse?.errorMetaData?.itemLimitExceeded;

    if (isEmpty(itemIds)) {
        return [];
    }

    const imCartItems = cartResponse?.data?.imCartItems || [];
    const result: ItemLimitExceededItem[] = [];

    itemIds.forEach((itemId) => {
        // Find the cart item that matches this itemId
        const cartItem = imCartItems.find((item) => {
            // Check if any variation in the item's metadata matches the itemId
            return item.metadata?.variations?.some((variation) => variation.id === itemId);
        });

        if (!isEmpty(cartItem)) {
            // Find the specific variation that matches the itemId
            const matchingVariation = cartItem.metadata?.variations?.find((variation) => variation.id === itemId);

            if (!isEmpty(matchingVariation)) {
                // Calculate the exceeded quantity: max_allowed_quantity - current quantity
                const maxAllowedQuantity = cartItem.metadata?.max_allowed_quantity || 0;
                const currentQuantity = cartItem.quantity || 0;
                const exceededQuantity = currentQuantity - maxAllowedQuantity;

                // Extract the required data
                const title = cartItem.name || matchingVariation.display_name || "";
                const imageId = cartItem.imageIds?.[0] || matchingVariation.images?.[0] || "";

                if (!isEmpty(title) && exceededQuantity > 0) {
                    result.push({
                        title,
                        quantity: exceededQuantity,
                        imageId,
                        itemId: cartItem?.itemId,
                    });
                }
            }
        }
    });

    return result;
};

export const buildErrorData = (
    cartResponse: GiftingReviewedCart | null,
): {
    maxAllowedCartWeightInGrams?: number;
    maxAllowedCartVolumeInCc?: number;
    restaurantName?: string;
    itemLimitExceededList?: ItemLimitExceededItem[];
} => {
    return {
        maxAllowedCartWeightInGrams: cartResponse?.data?.imStoresInfo?.[0]?.maxAllowedCartWeightInGrams, //TODO: currently we are using first storeinfo, this will break in scenario where there are multiple stores
        maxAllowedCartVolumeInCc: cartResponse?.data?.imStoresInfo?.[0]?.maxAllowedCartVolumeInCc,
        restaurantName: cartResponse?.data?.restaurant_details?.name || STATIC_TEXTS.RESTAURANT,
        itemLimitExceededList: getItemLimitExceededList(cartResponse),
    };
};

export const removeExcessItemsFromGiftingCart = (
    localCart: GiftingLocalCart,
    cartResponse: GiftingReviewedCart | null,
    itemLimitExceededList: ItemLimitExceededItem[],
): GiftingLocalCart => {
    const updatedLocalCart = { ...localCart };

    // Process each exceeded item and reduce quantity by the excess amount
    itemLimitExceededList.forEach((exceededItem) => {
        // Find and update IM cart items
        if (!isEmpty(updatedLocalCart.imCartItems) && !isEmpty(cartResponse?.data?.imCartItems)) {
            updatedLocalCart.imCartItems = updatedLocalCart.imCartItems.map((localItem) => {
                // Find the corresponding reviewed cart item
                const reviewedCartItem = cartResponse?.data?.imCartItems?.find(
                    (reviewedItem) => reviewedItem.itemId === localItem.itemId,
                );

                if (!isEmpty(reviewedCartItem)) {
                    if (reviewedCartItem.itemId === exceededItem.itemId) {
                        // Reduce quantity by the excess amount
                        const newQuantity = Math.max(0, localItem.quantity - exceededItem.quantity);
                        return { ...localItem, quantity: newQuantity };
                    }
                }
                return localItem;
            });
        }
    });

    return updatedLocalCart;
};

export const getRestaurantNameForGiftingCart = (cartResponse: GiftingReviewedCart): string | undefined => {
    // if food only cart, return restaurant name, rest return gift store header
    if (!isEmpty(cartResponse?.data?.foodCartItems?.items) && isEmpty(cartResponse?.data?.imCartItems)) {
        return cartResponse.data?.restaurant_details?.name || STATIC_TEXTS.GIFT_STORE_HEADER;
    }
    return STATIC_TEXTS.GIFT_STORE_HEADER;
};

// food subcart is always a single delivery, IM can have multiple deliveries
export const getNumberOfDeliveriesForGiftingCart = (cartResponse: GiftingReviewedCart): number => {
    const { foodCartItems, imShipmentDetails } = cartResponse?.data || {};
    const foodDelCount = !isEmpty(foodCartItems?.items) ? 1 : 0;
    const imDelCount = !isEmpty(imShipmentDetails) ? imShipmentDetails.length : 0;
    return foodDelCount + imDelCount;
};
